import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CiSearch } from "react-icons/ci";
import { TailSpin } from "react-loader-spinner";
import toast from "react-hot-toast";

import { DataTable } from "../../components/data-table";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import { PROD_LINK, STAGING_LINK } from "../../constant";
import { IS_STAGING } from "../../constant/api";
import {
  apiQueryKey,
  type CreateInviteCodeBody,
  type InviteCodeApi,
  useApiPatchMutation,
  useApiPostMutation,
  useInviteCodesMetricsQuery,
  useInviteCodesQuery,
} from "../../lib/api";
import CreateInviteCodeModal from "./CreateInviteCodeModal";
import InviteCodeAnalyticsPanel from "./InviteCodeAnalytics";
import InviteCodeDetailModal from "./InviteCodeDetailModal";
import InviteCodeStats from "./InviteCodeStats";
import type { InviteCodeStatusFilter } from "./inviteCodesTypes";
import { statusFilterToApi } from "./inviteCodesTypes";
import {
  buildInviteCodeTableRows,
  createInviteCodeTableColumns,
  INVITE_CODES_TABLE_LIMIT,
  type InviteCodeTableRow,
} from "./inviteCodesTable";

const STATUS_TABS: { id: InviteCodeStatusFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "used", label: "Used" },
  { id: "expired", label: "Expired" },
  { id: "deactivated", label: "Deactivated" },
];

function errMessage(err: unknown): string {
  if (
    err &&
    typeof err === "object" &&
    "response" in err &&
    err.response &&
    typeof err.response === "object" &&
    "data" in err.response &&
    err.response.data &&
    typeof err.response.data === "object" &&
    "message" in err.response.data
  ) {
    const m = (err.response.data as { message?: unknown }).message;
    if (typeof m === "string") return m;
  }
  return "Could not load invite codes.";
}

const InviteCodesOverview: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] =
    useState<InviteCodeStatusFilter>("all");
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState<InviteCodeApi | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const apiStatus = statusFilterToApi(statusFilter);

  const {
    data: metrics,
    isPending: metricsPending,
    isError: metricsError,
    refetch: refetchMetrics,
  } = useInviteCodesMetricsQuery();

  const { data, isPending, isError, error, refetch, isFetching } =
    useInviteCodesQuery({
      page,
      limit: INVITE_CODES_TABLE_LIMIT,
      search,
      status: apiStatus,
    });

  const codes = data?.codes ?? [];
  const pagination = data?.pagination;
  const totalPages = Math.max(1, pagination?.pages ?? 1);
  const totalCount = pagination?.total ?? codes.length;

  const tableRows = useMemo(() => buildInviteCodeTableRows(codes), [codes]);

  const handleCopy = useCallback(async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Code copied");
    } catch {
      toast.error("Could not copy code");
    }
  }, []);

  const openDetail = useCallback((row: InviteCodeTableRow) => {
    setSelected(row.raw);
    setDetailOpen(true);
  }, []);

  const columns = useMemo(
    () =>
      createInviteCodeTableColumns({
        onCopy: handleCopy,
        onView: openDetail,
      }),
    [handleCopy, openDetail]
  );

  const invalidateList = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: apiQueryKey("invite-codes"),
    });
    queryClient.invalidateQueries({
      queryKey: apiQueryKey("invite-codes-metrics"),
    });
  }, [queryClient]);

  const createMutation = useApiPostMutation<unknown, CreateInviteCodeBody>(
    "api/invite-codes",
    {
      onSuccess: () => {
        toast.success("Invite code created");
        setCreateOpen(false);
        invalidateList();
      },
      onError: (e) => {
        toast.error(errMessage(e), { duration: 4000 });
      },
    }
  );

  const handleCreate = useCallback(
    (body: CreateInviteCodeBody) => {
      createMutation.mutate(body);
    },
    [createMutation]
  );

  const deactivateMutation = useApiPatchMutation<
    unknown,
    { id: string; reason: string }
  >((v) => `api/invite-codes/${v.id}/deactivate`, {
    body: (v) => ({ reason: v.reason }),
    onSuccess: (_res, variables) => {
      toast.success("Invite code deactivated");
      setSelected((prev) =>
        prev && prev._id === variables.id
          ? { ...prev, status: "DEACTIVATED" }
          : prev
      );
      invalidateList();
    },
    onError: (e) => {
      toast.error(errMessage(e), { duration: 4000 });
    },
  });

  const handleDeactivate = useCallback(
    (id: string, reason: string) => {
      deactivateMutation.mutate({ id, reason });
    },
    [deactivateMutation]
  );

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden">
      <div className="flex h-[31px] w-full shrink-0 items-center justify-center bg-[#1B1B1B]">
        {IS_STAGING ? (
          <h4 className="text-[12px] font-medium text-[#AEBCC6]">
            You are in Staging:{" "}
            <a href={PROD_LINK} className="text-[#03C668]">
              Switch to Prod
            </a>
          </h4>
        ) : (
          <h4 className="text-[12px] font-medium text-[#AEBCC6]">
            You are in PROD:{" "}
            <a href={STAGING_LINK} className="text-[#DF4E4E]">
              Switch to Staging
            </a>
          </h4>
        )}
      </div>

      <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden lgss:flex-row">
        <Sidebar />
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto bg-[#13181B] lgss:w-10/12">
          <Navbar />
          <div className="mx-auto w-full max-w-[1600px] flex-1 px-5 pb-24 pt-6 font-manrope mds:px-8 lgss:px-10">
            <header className="mb-6">
              <h1 className="font-sora text-xl font-semibold tracking-tight text-white">
                Invite Codes
              </h1>
              <p className="mt-1 max-w-xl text-[13px] leading-relaxed text-[#869AA9]">
                Create and manage invite codes for user acquisition.
              </p>
            </header>

            <div className="space-y-6">
              <InviteCodeStats metrics={metrics} isLoading={metricsPending} />
              <InviteCodeAnalyticsPanel
                metrics={metrics}
                isLoading={metricsPending}
                isError={metricsError}
                onRetry={() => refetchMetrics()}
              />

              <section className="rounded-2xl border border-[#2C363E] bg-[#161E25]/40 p-4 sm:p-5">
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className="font-sora text-[16px] font-semibold text-white">
                      Invite Codes
                    </h2>
                    <p className="mt-1 text-[13px] text-[#869AA9]">
                      Create and manage invite codes for user acquisition.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCreateOpen(true)}
                    className="inline-flex h-10 shrink-0 items-center justify-center gap-1 rounded-xl bg-secondary_button px-4 text-[13px] font-semibold text-white shadow-[0_8px_24px_rgba(11,132,203,0.35)] transition hover:brightness-110"
                  >
                    + Create Code
                  </button>
                </div>

                <div className="mb-3 flex h-11 items-center gap-2 rounded-xl border border-[#2C363E] bg-[#13181B] px-3">
                  <CiSearch className="shrink-0 text-[22px] text-[#869AA9]" />
                  <input
                    type="search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search codes or users..."
                    className="w-full bg-transparent text-[13px] text-[#E7EBEE] outline-none placeholder:text-[#5C6B76]"
                  />
                </div>

                <div
                  className="mb-4 flex flex-wrap gap-2"
                  role="tablist"
                  aria-label="Status filter"
                >
                  {STATUS_TABS.map((tab) => {
                    const active = statusFilter === tab.id;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => setStatusFilter(tab.id)}
                        className={`rounded-full px-3.5 py-1.5 text-[12px] font-semibold transition border ${
                          active
                            ? "border-secondary_button bg-secondary_button text-white"
                            : "border-[#2C363E] bg-[#1C2227] text-[#869AA9] hover:border-[#3A4852] hover:text-[#C6D0D7]"
                        }`}
                      >
                        {tab.label}
                      </button>
                    );
                  })}
                </div>

                {isFetching && !isPending && (
                  <p className="mb-2 text-[12px] text-[#869AA9]">
                    Updating list…
                  </p>
                )}

                {isError && (
                  <div className="mb-4 flex flex-col gap-2 rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-3 text-[13px] text-red-100/95 sm:flex-row sm:items-center sm:justify-between">
                    <span>{errMessage(error)}</span>
                    <button
                      type="button"
                      onClick={() => refetch()}
                      className="shrink-0 rounded-lg border border-red-500/40 bg-red-500/20 px-3 py-1.5 text-[12px] font-semibold text-red-200"
                    >
                      Retry
                    </button>
                  </div>
                )}

                {isPending && !isError && (
                  <div className="flex justify-center rounded-xl border border-[#2C363E] bg-[#161E25]/40 py-16">
                    <TailSpin color="#0B75C2" height={36} width={36} />
                  </div>
                )}

                {!isPending && !isError && (
                  <DataTable<InviteCodeTableRow>
                    columns={columns}
                    data={tableRows}
                    currentPage={page}
                    setCurrentPage={setPage}
                    totalPage={totalPages}
                    count={totalCount}
                    limit={INVITE_CODES_TABLE_LIMIT}
                    onRowClick={openDetail}
                    emptyContent="No invite codes match this filter."
                    className="overflow-hidden rounded-xl border border-[#2C363E]"
                    fixedBodyHeight="min(520px, 50vh)"
                  />
                )}
              </section>
            </div>
          </div>
        </div>
      </div>

      <CreateInviteCodeModal
        open={createOpen}
        onClose={() => {
          if (!createMutation.isPending) setCreateOpen(false);
        }}
        submitting={createMutation.isPending}
        onCreate={handleCreate}
      />
      <InviteCodeDetailModal
        open={detailOpen}
        code={selected}
        onClose={() => {
          if (!deactivateMutation.isPending) {
            setDetailOpen(false);
            setSelected(null);
          }
        }}
        deactivating={deactivateMutation.isPending}
        onDeactivate={handleDeactivate}
      />
    </div>
  );
};

export default InviteCodesOverview;
